import Phaser from 'phaser';
import {
  publicRankings,
  publicUserStats,
  type PublicRankingRow,
  type PublicUserStats,
} from '../network/api.js';
import { getUser } from '../network/session.js';
import { resetOverlay } from '../ui/overlay.js';
import { mountBackdrop } from '../ui/backdrop.js';
import { transitionTo } from '../ui/transition.js';

export class HallScene extends Phaser.Scene {
  private overlay: HTMLDivElement | null = null;

  constructor() {
    super('Hall');
  }

  create(): void {
    resetOverlay();

    mountBackdrop(this, { color: 0x1a0508, depth: -10 });

    this.mountUI();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unmountUI());
  }

  private goTo(key: string, data?: object): void {
    this.unmountUI();
    transitionTo(this, key, data);
  }

  private mountUI(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const wrap = document.createElement('div');
    wrap.style.cssText = `
      position: absolute; inset: 0;
      display: grid; grid-template-rows: 64px 1fr;
      pointer-events: auto; box-sizing: border-box;
    `;
    wrap.innerHTML = `
      <header style="display:flex; align-items:center; padding:0 24px; background: linear-gradient(180deg, rgba(245,200,105,0.10) 0%, rgba(0,0,0,0) 100%); border-bottom:1px solid rgba(245,200,105,0.25); gap:14px;">
        <button id="dm-back" class="lux-btn-ghost" style="padding:6px 14px;">← 로비</button>
        <div style="font-size:18px; font-weight:800; color:#f5c869; letter-spacing:0.5px;">🏆 명예의 전당</div>
        <div style="flex:1;"></div>
        <div style="font-size:12px; color:rgba(244,236,214,0.7);">${escapeHtml(getUser()?.username ?? '')}</div>
      </header>
      <div style="padding:18px; min-height:0; box-sizing:border-box; display:flex; justify-content:center;">
        <section class="lux-panel" style="width:760px; max-width:100%;">
          <h3 style="margin:0 0 6px;">플레이어 순위</h3>
          <div style="font-size:11px; color:rgba(244,236,214,0.5); margin-bottom:8px;">레이팅 기준 · 완료된 게임만 집계 · 사용자를 클릭하면 상세 전적을 볼 수 있습니다</div>
          <div class="lux-divider"></div>
          <div id="dm-hall-list" class="lux-scroll" style="overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:6px; padding-right:4px; min-height:0;"></div>
        </section>
      </div>
    `;
    root.appendChild(wrap);
    this.overlay = wrap;

    wrap
      .querySelector<HTMLButtonElement>('#dm-back')!
      .addEventListener('click', () => this.goTo('Lobby'));

    void this.loadRankings();
  }

  private async loadRankings(): Promise<void> {
    const list = this.overlay?.querySelector<HTMLDivElement>('#dm-hall-list');
    if (!list) return;
    list.innerHTML = `<div style="padding:40px; text-align:center; color:rgba(244,236,214,0.5);">불러오는 중…</div>`;
    try {
      const { rankings } = await publicRankings();
      if (this.overlay?.querySelector('#dm-hall-list') !== list) return;
      if (rankings.length === 0) {
        list.innerHTML = `<div style="padding:40px; text-align:center; color:rgba(244,236,214,0.4);">아직 순위가 없습니다</div>`;
        return;
      }
      const me = getUser()?.id;
      list.innerHTML = rankings.map((r) => rowHtml(r, r.id === me)).join('');
      list.querySelectorAll<HTMLDivElement>('[data-id]').forEach((row) => {
        row.addEventListener('click', () =>
          this.openStats(row.dataset.id!, row.dataset.name ?? ''),
        );
      });
    } catch (err) {
      list.innerHTML = `<div style="padding:40px; text-align:center; color:#ffb878;">불러오기 실패: ${escapeHtml(
        (err as Error).message,
      )}</div>`;
    }
  }

  private async openStats(id: string, name: string): Promise<void> {
    const root = document.getElementById('overlay');
    if (!root) return;
    const backdrop = document.createElement('div');
    backdrop.style.cssText = `
      position: fixed; inset: 0; background: rgba(10,4,6,0.75);
      backdrop-filter: blur(4px); display:flex; align-items:center;
      justify-content:center; pointer-events:auto; z-index:200;
    `;
    const modal = document.createElement('div');
    modal.className = 'lux-panel';
    modal.style.cssText =
      'width:520px; max-width:92vw; max-height:84vh; overflow:auto; pointer-events:auto; padding:22px;';
    modal.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <h3 style="margin:0;">${escapeHtml(name)} 전적</h3>
        <button id="dm-modal-close" class="lux-btn-ghost" style="padding:4px 10px; font-size:14px;">✕</button>
      </div>
      <div class="lux-divider"></div>
      <div id="dm-stats-body" style="margin-top:8px; color:rgba(244,236,214,0.6); text-align:center; padding:30px;">불러오는 중…</div>
    `;
    backdrop.appendChild(modal);
    root.appendChild(backdrop);
    const close = (): void => backdrop.remove();
    modal
      .querySelector<HTMLButtonElement>('#dm-modal-close')!
      .addEventListener('click', close);
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });

    const body = modal.querySelector<HTMLDivElement>('#dm-stats-body')!;
    try {
      const stats = await publicUserStats(id);
      body.style.color = '';
      body.style.textAlign = '';
      body.style.padding = '';
      body.innerHTML = statsHtml(stats);
    } catch (err) {
      body.innerHTML = `<span style="color:#ffb878;">불러오기 실패: ${escapeHtml(
        (err as Error).message,
      )}</span>`;
    }
  }

  private unmountUI(): void {
    this.overlay?.remove();
    this.overlay = null;
  }
}

function statsHtml(s: PublicUserStats): string {
  if (s.games === 0) {
    return `<div style="padding:24px; text-align:center; color:rgba(244,236,214,0.45);">완료된 게임 기록이 없습니다</div>`;
  }
  const maxCount = Math.max(...s.placements.map((p) => p.count), 1);
  const pct = (r: number): string => `${(r * 100).toFixed(1)}%`;
  const bars = s.placements
    .map((p) => {
      const w = Math.round((p.count / maxCount) * 100);
      const isWin = p.placement === 0;
      const barColor = isWin
        ? 'linear-gradient(90deg, #f5c869, #c89230)'
        : 'linear-gradient(90deg, rgba(245,200,105,0.5), rgba(245,200,105,0.25))';
      return `
        <div style="display:flex; align-items:center; gap:10px;">
          <div style="width:48px; text-align:right; font-weight:700; color:${isWin ? '#f5c869' : '#f4ecd6'};">${p.placement + 1}위</div>
          <div style="flex:1; background:rgba(0,0,0,0.25); border-radius:6px; height:20px; overflow:hidden;">
            <div style="width:${w}%; height:100%; background:${barColor};"></div>
          </div>
          <div style="width:110px; font-size:12px; color:rgba(244,236,214,0.8); text-align:right;">
            ${p.count}회 · <b style="color:#f5c869;">${pct(p.rate)}</b>
          </div>
        </div>`;
    })
    .join('');
  const stat = (label: string, value: string): string => `
    <div style="flex:1; background:rgba(255,255,255,0.04); border:1px solid rgba(245,200,105,0.15); border-radius:8px; padding:8px; text-align:center;">
      <div style="font-size:10px; color:#8a5e1f; font-weight:700;">${label}</div>
      <div style="font-size:16px; font-weight:800; color:#f4ecd6; margin-top:2px;">${value}</div>
    </div>`;
  const titles =
    s.titles.length > 0
      ? `<div style="margin-top:14px;">
           <div style="font-size:11px; color:#f5c869; font-weight:700; margin-bottom:6px;">직위 이력</div>
           <div style="display:flex; flex-wrap:wrap; gap:6px;">
             ${s.titles
               .map(
                 (t) =>
                   `<span class="lux-chip" style="background:rgba(245,200,105,0.15); color:#f4ecd6;">${escapeHtml(t.title)} ${t.count}</span>`,
               )
               .join('')}
           </div>
         </div>`
      : '';
  return `
    <div style="display:flex; gap:8px; margin-bottom:6px;">
      ${stat('레이팅', String(s.user.rating))}
      ${stat('총 게임', `${s.games}판`)}
      ${stat('1위', `${s.wins}회`)}
      ${stat('승률', pct(s.winRate))}
    </div>
    <div style="display:flex; gap:8px; margin-bottom:16px;">
      ${stat('평균 순위', s.avgPlacement === null ? '-' : `${(s.avgPlacement + 1).toFixed(2)}위`)}
      ${stat('최고 순위', s.bestPlacement === null ? '-' : `${s.bestPlacement + 1}위`)}
    </div>
    <div style="font-size:11px; color:#f5c869; font-weight:700; margin-bottom:8px;">순위별 분포</div>
    <div style="display:flex; flex-direction:column; gap:6px;">${bars}</div>
    ${titles}`;
}

function rowHtml(r: PublicRankingRow, isMe: boolean): string {
  const medal =
    r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : '';
  const rankBadge = medal
    ? `<div style="width:40px; text-align:center; font-size:22px;">${medal}</div>`
    : `<div style="width:40px; text-align:center; font-weight:900; color:#f5c869; font-size:16px;">${r.rank}</div>`;
  const top = r.rank <= 3;
  const winRate = r.games > 0 ? Math.round((r.wins / r.games) * 100) : 0;
  const initial = (r.username || '?').slice(0, 1).toUpperCase();
  const border = isMe
    ? '1px solid rgba(130,221,156,0.6)'
    : top
      ? '1px solid rgba(245,200,105,0.45)'
      : '1px solid rgba(245,200,105,0.12)';
  const glow = top ? 'box-shadow:0 0 14px rgba(245,200,105,0.12);' : '';
  return `
    <div class="lux-list-row" data-id="${escapeHtml(r.id)}" data-name="${escapeHtml(r.username)}" style="display:flex; align-items:center; gap:12px; border:${border}; ${glow} cursor:pointer;">
      ${rankBadge}
      <div style="width:34px; height:34px; border-radius:50%; background: linear-gradient(135deg, #c89b2f 0%, #8a5e1f 100%); display:flex; align-items:center; justify-content:center; font-weight:700; color:#fff; font-size:14px; flex-shrink:0;">${escapeHtml(initial)}</div>
      <div style="flex:1; min-width:0;">
        <div style="font-weight:700; color:#f4ecd6; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">
          ${escapeHtml(r.username)}${isMe ? '<span style="color:#82dd9c; font-size:11px; margin-left:6px;">(나)</span>' : ''}
        </div>
        <div style="font-size:11px; color:rgba(244,236,214,0.55);">${r.games}판 · ${r.wins}승 (${winRate}%)</div>
      </div>
      <div style="text-align:right; flex-shrink:0;">
        <div style="font-size:18px; font-weight:900; color:#f5c869;">${r.rating}</div>
        <div style="font-size:10px; color:rgba(244,236,214,0.45); letter-spacing:0.5px;">RATING</div>
      </div>
    </div>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
